import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  faCalendar,
  faEnvelope,
  faLocationArrow,
  faMobile,
  faPen,
  faRestroom,
  faSave,
  faTrash,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';
import { Country, otherCountries, prioCountries } from '../../../services/kingfisher/countries';
import { FidelityService } from '../../../services/fidelity/fidelity.service';
import { ToastService } from '../../../services/toast.service';
import { FidelityCardTypeEnum, IsNewTenantCh, MemberTypeCh } from '../../../services/fidelity/fidelity';
import { HonorificPrefix } from '../../../services/kingfisher/customer';
import { HonorificPrefixKf } from '../../../services/fidelity/customer-fidelity';
import { UpdateCustomerComplete } from '../../../services/fidelity/update-customer-fidelity';
import { Subscribable } from '../../../stores/tunnel/model/subscribable';
import { TunnelDataStore } from '../../../stores/tunnel/tunnel-data.store';
import { CustomerState } from '../../../stores/tunnel/model/customer-state';
import {
  BuildCustomerCivilityFormGroup,
  BuildCustomerAddressFormGroup,
  BuildCustomerInfoFormGroup,
  BuildMarketingConsentFormGroup,
} from '../../../models/register/form-groups';
import { initCalendarTranslation } from '../../../services/helpers/primeng-calendar-helper';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { FeatureSettingsService } from '../../../services/feature-settings/feature-settings.service';

type RadioButtonValue = {
  name: string;
  value: HonorificPrefix;
};

@Component({
  selector: 'ng-casto-customer-info-details',
  templateUrl: './customer-info-details.component.html',
})
export class CustomerInfoDetailsComponent extends Subscribable implements OnInit, OnDestroy {
  protected form: FormGroup;
  protected civilityForm: FormGroup;
  protected addressForm: FormGroup;
  protected marketingConsentForm: FormGroup;
  private _editable: boolean = false;
  protected loading: boolean = false;
  protected countries: Country[] = [];

  protected civilityValues: RadioButtonValue[] = [
    {
      name: this.translate.instant('customer-info-form.civility-form.m'),
      value: HonorificPrefix.Monsieur,
    },
    {
      name: this.translate.instant('customer-info-form.civility-form.mme'),
      value: HonorificPrefix.Madame,
    },
    {
      name: this.translate.instant('customer-info-form.civility-form.mmle'),
      value: HonorificPrefix.Mademoiselle,
    },
  ];

  private currentCustomer: CustomerState;

  get editable(): boolean {
    return this._editable;
  }

  set editable(value: boolean) {
    this._editable = value;
    this.switchFormEnable();
  }

  constructor(
    private readonly fidelityService: FidelityService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly primeNGConfig: PrimeNGConfig,
    private readonly tunnelDataStore: TunnelDataStore,
    private readonly translate: TranslateService,
    private readonly featureSettingsService: FeatureSettingsService
  ) {
    super();
  }

  ngOnInit() {
    super.subscribe();
  }

  ngOnDestroy(): void {
    super.unsubscribe();
  }

  protected fidelityEnabled(): boolean {
    return this.featureSettingsService.fidelityEnabled;
  }

  protected subscribeToObservables(): void {
    this.subscriptions.push(
      this.tunnelDataStore.getCurrentCustomer().subscribe(customer => {
        if (customer) {
          this.currentCustomer = customer;
          if (!this.form) {
            this.initForm();
          }
        }
      })
    );
  }

  private initCountries() {
    this.countries = this.countries.concat(prioCountries, otherCountries);
  }

  private initBirthdateCalendar() {
    initCalendarTranslation(this.primeNGConfig);
  }

  protected isAddressFormControlVisible(attr: string): boolean {
    return this.addressForm?.get(attr)?.value || this.editable;
  }

  protected isInputRequiredError(attr: string, subform?: string): boolean {
    if (subform) {
      return this.form?.get(subform)?.get(attr)?.errors?.required;
    }
    return this.form?.get(attr)?.errors?.required;
  }

  protected async submitForm(): Promise<void> {
    if (this.form?.valid) {
      await this.updateUserInfo();
    }
  }

  private convertToHonorificPrefixKf(prefix: HonorificPrefix): HonorificPrefixKf {
    switch (prefix) {
      case HonorificPrefix.Monsieur:
        return HonorificPrefixKf.Monsieur;
      case HonorificPrefix.Madame:
        return HonorificPrefixKf.Madame;
      case HonorificPrefix.Mademoiselle:
        return HonorificPrefixKf.Mademoiselle;
      default:
        throw new Error(`Unknown HonorificPrefix: ${prefix}`);
    }
  }

  private async buildUpdateCustomerInput(): Promise<UpdateCustomerComplete> {
    let updateCustomerComplete: UpdateCustomerComplete = {
      customerIdKf: this.currentCustomer?.userData.kfId,
      civility: this.convertToHonorificPrefixKf(this.civilityForm.get('civility').value),
      lastName: this.civilityForm.get('lastname').value,
      firstName: this.civilityForm.get('firstname').value,
      email: this.civilityForm.get('email').value,
      phoneNumber: this.civilityForm.get('mobile').value,
      houseNumber: this.addressForm.get('num').value,
      address: this.addressForm.get('line1').value,
      buildingFloorStaircase: this.addressForm.get('line2').value,
      localityPoBox: this.addressForm.get('complement').value,
      postalCode: this.addressForm.get('postalCode').value,
      city: this.addressForm.get('city').value,
      country: (this.addressForm.get('country').value as Country)?.value,
      birthdate: new Date((this.civilityForm.get('birthdate').value as Date)?.setHours(12)),
      memberType: MemberTypeCh.NonRenseigne,
      cardIsPremium: this.currentCustomer?.fidelity?.typeCard === FidelityCardTypeEnum.PREMIUM,
      newMoveIn: false,
      //pas encore save depuis le formulaire

      isNewTenantCh: IsNewTenantCh.No,
      habitationType: 0,
      customerPropertySituation: 0,
      marketingConsent: {
        optinEmail: this.marketingConsentForm.get('email').value,
        optinSms: this.marketingConsentForm.get('sms').value,
        optinPhone: this.marketingConsentForm.get('phone').value,
        optoutPostal: this.marketingConsentForm.get('post').value,
      },
      siret: '',
      nTvaIntracommunautaire: '',
      projectNext6Months: {
        kitchen: false,
        bathroom: false,
        floorAndWall: false,
        insulation: false,
        storage: false,
        outdoor: false,
      },
      companyName: '',
    };

    return updateCustomerComplete;
  }

  private showErrorToast(msg: string) {
    this.toastService.showErrorToast(this.translate.instant('popins.signup.error_title'), msg);
  }

  private async updateUserInfo(): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      this.showErrorToast(
        "La mise à jour d'informations client est momentanément indisponible. Veuillez nous excusez."
      );
      return;
    }
    try {
      this.loading = true;
      let updateCustomerComplete: UpdateCustomerComplete = await this.buildUpdateCustomerInput();
      await this.fidelityService.updateCustomerFidelity(updateCustomerComplete);
      this.refreshLocalCustomerUserdatas(updateCustomerComplete);
      this.handleSuccess();
      this.loading = false;
    } catch (error) {
      this.loading = false;
      this.handleError(error);
    }
  }

  private handleSuccess() {
    this.showSuccess('popins.signup.success_update');
    this.editable = false;
  }

  private handleError(error: Error) {
    logError(error);
    this.showError(error.message);
    this.editable = true;
  }

  private refreshLocalCustomerUserdatas(updateCustomerComplete: UpdateCustomerComplete): void {
    let userData = {
      ...this.currentCustomer?.userData,
      firstname: updateCustomerComplete.firstName,
      lastname: updateCustomerComplete.lastName,
      email: updateCustomerComplete.email,
      phoneNumber: updateCustomerComplete.phoneNumber,
      dateOfBirth: updateCustomerComplete.birthdate.toDateString(),
      address: {
        ...this.currentCustomer?.userData.address,
        num: updateCustomerComplete.houseNumber,
        postalCode: updateCustomerComplete.postalCode,
        city: updateCustomerComplete.city,
        country: updateCustomerComplete.country,
        line1: updateCustomerComplete.address,
        line2: updateCustomerComplete.buildingFloorStaircase,
        complement: updateCustomerComplete.localityPoBox,
      },
      marketingConsent: {
        email: updateCustomerComplete.marketingConsent.optinEmail,
        sms: updateCustomerComplete.marketingConsent.optinSms,
        phone: updateCustomerComplete.marketingConsent.optinPhone,
        post: updateCustomerComplete.marketingConsent.optoutPostal,
      },
    };

    this.tunnelDataStore.updateCurrentCustomer({
      ...this.currentCustomer,
      userData: userData,
    });
  }

  private showSuccess(messageKey: string) {
    this.toastService.showSuccessToast(
      this.translateService.instant('generic.saved-success'),
      this.translateService.instant(messageKey)
    );
  }

  private showError(error: string) {
    this.toastService.showErrorToast(this.translateService.instant('generic.saved-failed'), error);
  }

  private switchFormEnable() {
    this.editable ? this.form.enable() : this.form.disable();
    this.form.get('civility').get('email').disable();
  }

  private initFormGroup() {
    if (!this.currentCustomer.userData) return;
    
    this.civilityForm = BuildCustomerCivilityFormGroup(this.currentCustomer.userData);
    this.addressForm = BuildCustomerAddressFormGroup(this.currentCustomer.userData.address);
    this.marketingConsentForm = BuildMarketingConsentFormGroup(this.currentCustomer.userData.marketingConsent);
    this.form = BuildCustomerInfoFormGroup({
      civilityFormGroup: this.civilityForm,
      addressFormGroup: this.addressForm,
      marketingConsentFormGroup: this.marketingConsentForm,
    });
    this.form.disable();
  }

  private initForm() {
    this.initCountries();
    this.initBirthdateCalendar();
    this.initFormGroup();
  }

  protected resetForm() {
    this.initForm();
    this.editable = false;
  }

  protected readonly faUser = faUser;
  protected readonly faEnvelope = faEnvelope;
  protected readonly faMobile = faMobile;
  protected readonly faLocationArrow = faLocationArrow;
  protected readonly faCalendar = faCalendar;
  protected readonly faPen = faPen;
  protected readonly faSave = faSave;
  protected readonly faTrash = faTrash;
  protected readonly faRestroom = faRestroom;
}
